import { Reveal } from '../components/motion';
import SEO from '../components/SEO';
import { SectionTag, ArrowBtn, PageHero, FAQ } from '../components/bits';
import { useLead } from '../components/LeadModal';
import { useContent } from '../lib/content';
import { DIGITAL_ASSETS, LEARN_BLOCKS, STUDENT_BENEFITS, ALUMNI_BENEFITS, STUDENT_FAQ, IMAGES } from '../content/data';

const WHO = [
  { t: 'After Class 12', d: 'Start developing focused digital capabilities at an early stage.' },
  { t: 'College Students', d: 'Build practical expertise alongside conventional education.' },
  { t: 'Aspiring Creators', d: 'Turn content, technology and audience-building into a structured capability.' },
  { t: 'Future Freelancers & Entrepreneurs', d: 'Develop the skills required to acquire clients, build digital products and create independent income opportunities.' },
  { t: 'Professionals', d: "Upgrade existing capabilities, develop new digital and technology competencies, and use DITEC's practical ecosystem to strengthen career growth, consulting, leadership or entrepreneurial pathways." },
];

export default function Students() {
  const { open } = useLead();
  const { ctList } = useContent();
  return (
    <div data-testid="students-page">
    <SEO title="For Students" description="Build digital assets, develop practical skills and create evidence of what you can do with DITEC programmes." />
      <PageHero
        ck="students"
        tag="For Students"
        lines={<>Don't Just Prepare for a Career. <span className="font-serif italic font-normal text-electric">Start Building One.</span></>}
        intro="DITEC is designed for students who want to develop digital capability through practical work, personal digital assets, real projects and exposure to the market."
        image={IMAGES.students}
      >
        <ArrowBtn to="/programmes" variant="primary" testId="students-cta-programmes">Explore Programmes</ArrowBtn>
        <ArrowBtn to="/scholarship" variant="ghostDark" testId="students-cta-scholarship">Take the Scholarship Test</ArrowBtn>
      </PageHero>

      {/* Who Can Join */}
      <section className="bg-white py-20 sm:py-28">
        <div className="wrap">
          <SectionTag>01 — Who Can Join?</SectionTag>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ctList('students.who', WHO).map((w, i) => (
              <Reveal key={w.t} delay={i * 0.05} className={`rounded-3xl border border-navy/10 bg-paper p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10 ${i === 4 ? 'sm:col-span-2 lg:col-span-1' : ''}`}>
                <span className="font-mono text-xs text-electric/60">0{i + 1}</span>
                <h3 className="mt-3 text-lg font-semibold tracking-tight">{w.t}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-navy/65">{w.d}</p>
              </Reveal>
            ))}
            <Reveal delay={0.25} className="flex flex-col justify-between rounded-3xl bg-navy p-8 text-white">
              <p className="font-serif text-xl italic leading-snug text-gold">Build assets, not only certificates.</p>
              <button data-testid="who-join-cta" onClick={() => open('Student Programmes')} className="mt-6 w-fit text-sm font-semibold text-white underline decoration-gold decoration-2 underline-offset-4 hover:text-gold">Start the conversation</button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Four Digital Assets */}
      <section className="bg-grid-dark bg-navy py-20 text-white sm:py-28">
        <div className="wrap">
          <SectionTag dark>02 — What You Build</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 max-w-3xl font-semibold">Four Digital Assets. <span className="font-serif italic font-normal text-gold">One Growing Professional Identity.</span></h2></Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ctList('students.assets', DIGITAL_ASSETS).map((a, i) => (
              <Reveal key={a.title} delay={i * 0.07} className="rounded-3xl border border-white/10 bg-navy-800/70 p-8 transition-colors duration-500 hover:border-gold/40">
                <span className="font-mono text-3xl font-semibold text-gold/30">0{i + 1}</span>
                <h3 className="mt-5 text-xl font-semibold tracking-tight">{a.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{a.desc}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}><p className="mt-8 max-w-2xl text-sm leading-relaxed text-white/55 sm:text-base">These assets are progressively developed throughout the curriculum rather than created once and forgotten.</p></Reveal>
        </div>
      </section>

      {/* What You Learn */}
      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap">
          <SectionTag>03 — What You Learn</SectionTag>
          <div className="mt-12 grid gap-px overflow-hidden rounded-3xl border border-navy/10 bg-navy/10 md:grid-cols-2 lg:grid-cols-3">
            {ctList('students.learn', LEARN_BLOCKS).map((b) => (
              <div key={b.n} data-testid={`learn-block-${b.n}`} className="bg-white p-8 transition-colors duration-300 hover:bg-paper sm:p-9">
                <span className="font-mono text-xs text-electric">{b.n}</span>
                <h3 className="mt-3 text-lg font-semibold tracking-tight">{b.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-navy/65">{b.desc}</p>
              </div>
            ))}
          </div>
          <Reveal className="mt-10"><ArrowBtn to="/programmes" variant="navy" testId="students-cta-see-programmes">See the Programmes</ArrowBtn></Reveal>
        </div>
      </section>

      {/* Student Benefits */}
      <section className="bg-white py-20 sm:py-28">
        <div className="wrap">
          <SectionTag>04 — Student Benefits</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">What You Leave With <span className="font-serif italic font-normal text-electric">Matters.</span></h2></Reveal>
          <Reveal delay={0.08}><p className="mt-4 text-base text-navy/70 sm:text-lg">DITEC is designed around outcomes that remain useful beyond the classroom.</p></Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ctList('students.benefits', STUDENT_BENEFITS).map((b, i) => (
              <Reveal key={b.title} delay={i * 0.04} className="rounded-3xl border border-navy/10 bg-paper p-7">
                <h3 className="text-base font-semibold tracking-tight text-electric">{b.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-navy/65">{b.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Alumni */}
      <section id="alumni" className="bg-grid-dark bg-navy py-20 text-white sm:py-28">
        <div className="wrap">
          <SectionTag dark>05 — Alumni</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 max-w-3xl font-semibold">Graduation Is a <span className="font-serif italic font-normal text-gold">New Starting Point.</span></h2></Reveal>
          <Reveal delay={0.08}><p className="mt-4 text-base text-white/70 sm:text-lg">The DITEC relationship continues beyond the classroom.</p></Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ctList('students.alumni', ALUMNI_BENEFITS).map((b, i) => (
              <Reveal key={b.title} delay={i * 0.04} className="rounded-3xl border border-white/10 bg-navy-800/70 p-7 transition-colors duration-500 hover:border-gold/40">
                <h3 className="text-base font-semibold tracking-tight text-gold">{b.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-white/60">{b.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-paper py-20 sm:py-28">
        <div className="wrap max-w-4xl">
          <SectionTag>06 — Common Questions</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">Student <span className="font-serif italic font-normal text-electric">FAQ</span></h2></Reveal>
          <Reveal delay={0.1} className="mt-12"><FAQ items={STUDENT_FAQ} testId="student-faq" /></Reveal>
          <Reveal className="mt-10"><ArrowBtn onClick={() => open('Student Programmes')} variant="primary" testId="students-faq-cta">Still deciding? Talk to us</ArrowBtn></Reveal>
        </div>
      </section>
    </div>
  );
}
